import { c as _c } from "react/compiler-runtime";
import { AiLabel } from '@spa/pages-converters/cms/components/convertCmsImage';
import { ImageAiMarkerComponent } from '../image-ai-marker/image-ai-marker.component';
import ImageComponent from '@spa/components/image-component/image.component';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { ReactElement } from 'react';
import configEnv from '../../lib/image-loader/imageConfigEnv';
import { preload } from 'react-dom';
import styles from './picture.component.module.scss';
import useImagesLoader from '@spa/lib/react/hooks/useImagesLoader';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
export interface Props {
  /** Picture data */
  readonly picture: Picture;
  readonly priority: boolean | null;
  readonly enableNextJsLoader?: boolean;
  readonly fixedBreakPoint?: PictureBreakpoints;
}
export interface Picture {
  readonly alt: string;
  readonly id: string;
  readonly name?: string;
  readonly images: ReadonlyArray<Image>;
  readonly aiLabel?: AiLabel;
}
export interface Image {
  height: number;
  id: string;
  src: string;
  mimeType: string;
  width: number;
}
export enum PictureBreakpoints {
  MOBILE = 767,
  TABLET = 1023,
}
const allSizes = configEnv.imageSizes.concat(configEnv.deviceSizes).sort((a, b) => a - b);
export default function PictureComponent(t0) {
  const $ = _c(16);
  const {
    picture,
    priority,
    enableNextJsLoader: t1,
    fixedBreakPoint
  } = t0;
  const enableNextJsLoader = t1 === undefined ? true : t1;
  const pictureImages = picture.images;
  const sortedImages = [...pictureImages].sort(_temp);
  const largestImage = sortedImages[sortedImages.length - 1];
  const leastWideImageData = sortedImages[0];
  const {
    formatMessage
  } = useNamespacedIntl("image-ai-marker");
  const imagesLoader = useImagesLoader(enableNextJsLoader);
  let t2;
  if ($[0] !== imagesLoader) {
    t2 = image => {
      let nextWidth = image.width;
      for (const imageSize of allSizes) {
        if (imageSize >= nextWidth) {
          nextWidth = imageSize;
          break;
        }
      }
      return allSizes.filter(size => size <= nextWidth).map(imageSize_0 => `${imagesLoader({
        src: image.src,
        width: imageSize_0,
        quality: 75
      })} ${imageSize_0}w`).join(", ");
    };
    $[0] = imagesLoader;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const srcSets = sortedImages.map(t2);
  if (priority) {
    sortedImages.forEach((image_0, imageIdx) => {
      let media = "";
      if (sortedImages.length > 1) {
        if (leastWideImageData === image_0) {
          media = `(max-width: ${image_0.width}px)`;
        } else {
          if (largestImage === image_0) {
            media = `(min-width: ${sortedImages[imageIdx - 1].width}px)`;
          } else {
            media = `(min-width: ${sortedImages[imageIdx - 1].width}px) and (max-width: ${image_0.width}px)`;
          }
        }
      }
      const imageHref = imagesLoader({
        src: image_0.src,
        width: image_0.width,
        quality: 75
      });
      preload(imageHref, {
        as: "image",
        media,
        imageSrcSet: srcSets[imageIdx],
        fetchPriority: priority ? "high" : undefined
      });
    });
  }
  const isSvg = leastWideImageData.mimeType === "image/svg+xml";
  const aiLabelEnabled = picture.aiLabel?.enabled === true;
  let t3;
  if ($[2] !== aiLabelEnabled || $[3] !== formatMessage || $[4] !== picture.alt) {
    t3 = aiLabelEnabled && picture.alt ? `${picture.alt}, ${formatMessage("accessibleLabel")}` : picture.alt;
    $[2] = aiLabelEnabled;
    $[3] = formatMessage;
    $[4] = picture.alt;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const displayAlt = t3;
  let t4;
  if ($[6] !== aiLabelEnabled) {
    t4 = aiLabelEnabled ? {
      "data-digital-source-type": "compositeWithTrainedAlgorithmicMedia"
    } : {};
    $[6] = aiLabelEnabled;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const digitalSourceTypeAttr = t4;
  const t5 = styles;
  let t6;
  if ($[8] !== picture.aiLabel || $[9] !== picture.alt) {
    t6 = picture.aiLabel && <ImageAiMarkerComponent show={picture.aiLabel.enabled} position={picture.aiLabel.position} style={picture.aiLabel.style} alt={picture.alt} />;
    $[8] = picture.aiLabel;
    $[9] = picture.alt;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = isSvg ? <ImageComponent src={leastWideImageData.src} width={leastWideImageData.width} height={leastWideImageData.height} className={styles.picture__element} loading={priority ? "eager" : "lazy"} alt={displayAlt} mimeType={leastWideImageData.mimeType} {...digitalSourceTypeAttr} /> : <picture className={styles.picture__wrapper}>{sortedImages.map((image_1, imageIdx_0) => {
      const mediaQuery = largestImage !== image_1 ? `(max-width: ${fixedBreakPoint ?? image_1.width}px)` : "";
      return <source key={image_1.id + imageIdx_0} media={mediaQuery} srcSet={srcSets[imageIdx_0]} width={image_1.width} height={image_1.height} />;
    })}<ImageComponent src={leastWideImageData.src} width={leastWideImageData.width} height={leastWideImageData.height} className={styles.picture__element} loading={priority ? "eager" : "lazy"} fetchPriority={priority ? "high" : undefined} decoding={priority ? "sync" : "async"} alt={displayAlt} mimeType={leastWideImageData.mimeType} {...digitalSourceTypeAttr} /></picture>;
  let t8;
  if ($[11] !== displayAlt || $[12] !== t5.picture || $[13] !== t6 || $[14] !== t7) {
    t8 = <div className={t5.picture} aria-label={displayAlt}>{t6}{t7}</div>;
    $[11] = displayAlt;
    $[12] = t5.picture;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}
function _temp(imageA, imageB) {
  return imageA.width - imageB.width;
}